/**
 * Best-effort singular of a collection name, used to suggest what one record is called
 * ("Recipes" → "Recipe", "Abilities" → "Ability", "Missões" → "Missão", "Itens" → "Item").
 * Handles English and Portuguese, the languages Tomo is written for. It's only a suggestion:
 * people can always type their own.
 */

const IRREGULAR: Record<string, string> = {
  heroes: 'hero',
  people: 'person',
  children: 'child',
  men: 'man',
  women: 'woman',
  mice: 'mouse',
  geese: 'goose',
  teeth: 'tooth',
  feet: 'foot',
  dice: 'die',
  wolves: 'wolf',
  knives: 'knife',
  lives: 'life',
  leaves: 'leaf',
  elves: 'elf',
  dwarves: 'dwarf',
  thieves: 'thief',
  halves: 'half',
  staves: 'staff',
};

const IES: [RegExp, string] = [/([^aeiou])ies$/, '$1y'];

// Portuguese endings first: they are unambiguous and would otherwise lose only the final "s".
const RULES: [RegExp, string][] = [
  [/ões$/, 'ão'],
  [/ães$/, 'ão'],
  [/ais$/, 'al'],
  [/éis$/, 'el'],
  [/veis$/, 'vel'],
  [/ns$/, 'm'],
  [/(ss|us|is)$/, '$1'],
  IES,
  [/(s|x|z|ch|sh)es$/, '$1'],
  [/s$/, ''],
];

function singularWord(word: string): string {
  const lower = word.toLowerCase();
  const irregular = IRREGULAR[lower];
  if (irregular) return irregular;
  // Accented words are Portuguese, where "-ies" is not a plural of "-y" ("séries" → "série").
  const rules = /[^\x00-\x7f]/.test(lower) ? RULES.filter((rule) => rule !== IES) : RULES;
  for (const [re, to] of rules) if (re.test(lower)) return lower.replace(re, to);
  return lower;
}

/** Singular of the last word, keeping the rest and the first letter's case: "Hero items" → "Hero item". */
export function singularize(name: string): string {
  const trimmed = name.trim();
  const match = /^(.*?)(\S+)$/s.exec(trimmed);
  if (!match) return trimmed;
  const [, head, last] = match as unknown as [string, string, string];
  let one = singularWord(last);
  if (last[0] !== last[0]!.toLowerCase()) one = one[0]!.toUpperCase() + one.slice(1);
  return head + one;
}
